import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ClipboardCheck, Loader2, Plus, Download, AlertTriangle } from 'lucide-react';
import { convertToCSV, downloadCSV, formatDateForCSV } from '@/utils/csvExport';
import { SearchableSelect } from '@/components/SearchableSelect';

const INVENTORY_FIELDS = [
  { key: 'tyre1_brand', label: 'Tyre 1 Brand' },
  { key: 'tyre1_number', label: 'Tyre 1 Number' },
  { key: 'tyre2_brand', label: 'Tyre 2 Brand' },
  { key: 'tyre2_number', label: 'Tyre 2 Number' },
  { key: 'tyre3_brand', label: 'Tyre 3 Brand' },
  { key: 'tyre3_number', label: 'Tyre 3 Number' },
  { key: 'tyre4_brand', label: 'Tyre 4 Brand' },
  { key: 'tyre4_number', label: 'Tyre 4 Number' },
  { key: 'stepney_brand', label: 'Stepney Brand' },
  { key: 'stepney_number', label: 'Stepney Number' },
  { key: 'battery_brand', label: 'Battery Brand' },
  { key: 'battery_number', label: 'Battery Number' },
  { key: 'tool_kit_details', label: 'Tool Kit' },
];

const EMPTY_INVENTORY = INVENTORY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

const Audits = () => {
  const { API } = useAuth();
  const [audits, setAudits] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [auditTypeFilter, setAuditTypeFilter] = useState('');

  const [formData, setFormData] = useState({
    audit_type: 'Allotment',
    vehicle_id: '',
    driver_id: '',
    reason: '',
    submitted_by: '',
    ...EMPTY_INVENTORY,
  });

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [photos, setPhotos] = useState({
    car_side_1: null,
    car_side_2: null,
    car_side_3: null,
    car_side_4: null,
    driver_with_car: null
  });

  // Diff confirmation dialog state
  const [diffDialog, setDiffDialog] = useState({ open: false, diffs: [], previous: null });

  useEffect(() => {
    fetchData();
  }, [searchQuery, auditTypeFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (auditTypeFilter) params.audit_type = auditTypeFilter;
      const [auditsRes, driversRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/audits`, { params }),
        axios.get(`${API}/drivers`),
        axios.get(`${API}/vehicles`)
      ]);
      setAudits(auditsRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverSelect = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    setSelectedDriver(driver);
    setFormData({ ...formData, driver_id: driverId });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotos({ ...photos, [type]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Diff check: if a previous audit exists for this vehicle, compare inventory.
    // If anything differs, show confirmation popup instead of submitting directly.
    if (formData.vehicle_id) {
      try {
        const prev = await axios.get(`${API}/audits/last/${formData.vehicle_id}`);
        if (prev.data && typeof prev.data === 'object' && prev.data.id) {
          const diffs = [];
          for (const f of INVENTORY_FIELDS) {
            const oldVal = (prev.data[f.key] || '').toString().trim();
            const newVal = (formData[f.key] || '').toString().trim();
            // Only flag a diff if BOTH values are non-empty and differ.
            // (An empty new value just means user didn't fill it this time — not a mismatch.)
            if (oldVal && newVal && oldVal.toLowerCase() !== newVal.toLowerCase()) {
              diffs.push({ field: f.label, previous: oldVal, current: newVal });
            }
          }
          if (diffs.length > 0) {
            setDiffDialog({ open: true, diffs, previous: prev.data });
            return; // Wait for user confirmation
          }
        }
      } catch (err) {
        // Non-fatal — proceed to submit. (Endpoint may return null for first audit.)
      }
    }
    await submitAuditNow();
  };

  const submitAuditNow = async () => {
    setSubmitting(true);
    try {
      const inventoryPayload = INVENTORY_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: formData[f.key] || null }),
        {},
      );

      const auditData = {
        audit_type: formData.audit_type,
        vehicle_id: formData.vehicle_id,
        driver_id: formData.driver_id,
        reason: formData.reason,
        submitted_by: formData.audit_type === 'Parking' ? formData.submitted_by : formData.driver_id,
        ...inventoryPayload,
      };

      const response = await axios.post(`${API}/audits`, auditData);
      const auditId = response.data.id;

      // Upload photos
      for (const [type, file] of Object.entries(photos)) {
        if (file) {
          const base64 = await fileToBase64(file);
          await axios.post(`${API}/audits/${auditId}/upload`, {
            file_name: file.name,
            file_data: base64,
            photo_type: type,
          });
        }
      }

      toast.success('Audit submitted successfully');
      setDialogOpen(false);
      setDiffDialog({ open: false, diffs: [], previous: null });
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit audit');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      audit_type: 'Allotment',
      vehicle_id: '',
      driver_id: '',
      reason: '',
      submitted_by: '',
      ...EMPTY_INVENTORY,
    });
    setSelectedDriver(null);
    setPhotos({
      car_side_1: null,
      car_side_2: null,
      car_side_3: null,
      car_side_4: null,
      driver_with_car: null,
    });
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : 'Unknown';
  };

  const getVehicleNumber = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.vehicle_number : 'Unknown';
  };

  const handleExportCSV = () => {
    const headers = [
      { label: 'Audit ID', key: 'audit_id' },
      { label: 'Audit Type', key: 'audit_type' },
      { label: 'Vehicle Number', key: 'vehicle_number' },
      { label: 'Driver Name', key: 'driver_name' },
      { label: 'Driver Mobile', key: 'driver_mobile' },
      { label: 'Reason', key: 'reason' },
      { label: 'Created At', key: 'created_at' }
    ];

    const csvData = audits.map(audit => {
      const driver = drivers.find(d => d.id === audit.driver_id);
      const vehicle = vehicles.find(v => v.id === audit.vehicle_id);
      
      return {
        audit_id: audit.audit_id,
        audit_type: audit.audit_type,
        vehicle_number: vehicle?.vehicle_number || 'N/A',
        driver_name: driver?.name || 'N/A',
        driver_mobile: driver?.mobile || 'N/A',
        reason: audit.reason || '',
        created_at: formatDateForCSV(audit.created_at)
      };
    });

    const csv = convertToCSV(csvData, headers);
    const filename = `audits_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
    toast.success('Audits data exported successfully');
  };

  return (
    <div className="space-y-6" data-testid="audits-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Vehicle Audits</CardTitle>
              <CardDescription>Track allotments, parking, and repairs</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={audits.length === 0}
                data-testid="export-audits-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                className="btn-primary"
                onClick={() => setDialogOpen(true)}
                data-testid="new-audit-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Audit
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search & Filter */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Input
              placeholder="Search by Car No. or Driver ID/Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-72"
              data-testid="audit-search-input"
            />
            <select
              value={auditTypeFilter}
              onChange={(e) => setAuditTypeFilter(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="audit-type-filter"
            >
              <option value="">All Types</option>
              <option value="Allotment">Allotment</option>
              <option value="Parking">Parking</option>
              <option value="Repairs">Repairs</option>
            </select>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <Table data-testid="audits-table">
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Audit ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audits.map((audit) => (
                  <TableRow key={audit.id} className="table-row">
                    <TableCell className="font-mono">{audit.audit_id}</TableCell>
                    <TableCell>
                      <span className="status-badge status-active">
                        {audit.audit_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono">{getVehicleNumber(audit.vehicle_id)}</TableCell>
                    <TableCell>{getDriverName(audit.driver_id)}</TableCell>
                    <TableCell>{new Date(audit.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className="status-badge status-completed">Completed</span>
                    </TableCell>
                  </TableRow>
                ))}
                {audits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No audits found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Audit Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="audit-form-dialog">
          <DialogHeader>
            <DialogTitle>New Vehicle Audit</DialogTitle>
            <DialogDescription>Record audit details and upload photos</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Audit Type */}
            <div className="space-y-2">
              <Label htmlFor="audit_type">Audit Type *</Label>
              <select
                id="audit_type"
                value={formData.audit_type}
                onChange={(e) => {
                  setFormData({ ...formData, audit_type: e.target.value });
                  setSelectedDriver(null);
                }}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                data-testid="audit-type-select"
              >
                <option value="Allotment">Allotment</option>
                <option value="Parking">Parking</option>
                <option value="Repairs">Repairs</option>
              </select>
            </div>

            {/* Vehicle Selection */}
            <div className="space-y-2">
              <Label htmlFor="vehicle_id">Car No *</Label>
              <SearchableSelect
                options={vehicles.map((vehicle) => ({
                  value: vehicle.id,
                  label: `${vehicle.vehicle_number} - ${vehicle.make} ${vehicle.model}`,
                  keywords: [vehicle.vehicle_number, vehicle.make, vehicle.model, vehicle.vehicle_id].filter(Boolean),
                }))}
                value={formData.vehicle_id}
                onChange={(val) => setFormData({ ...formData, vehicle_id: val })}
                placeholder="Select vehicle"
                searchPlaceholder="Type car number, make, or model..."
                emptyText="No matching vehicles."
                testId="audit-vehicle-select"
              />
            </div>

            {/* Driver Selection (for Allotment and Repairs) */}
            {(formData.audit_type === 'Allotment' || formData.audit_type === 'Repairs') && (
              <div className="space-y-2">
                <Label htmlFor="driver_id">Driver ID *</Label>
                <SearchableSelect
                  options={drivers.map((driver) => ({
                    value: driver.id,
                    label: `${driver.driver_id} - ${driver.name}`,
                    keywords: [driver.driver_id, driver.name, driver.mobile].filter(Boolean),
                  }))}
                  value={formData.driver_id}
                  onChange={(val) => handleDriverSelect(val)}
                  placeholder="Select driver"
                  searchPlaceholder="Type driver ID, name, or mobile..."
                  emptyText="No matching drivers."
                  testId="audit-driver-select"
                />

                {/* Auto-filled Driver Details */}
                {selectedDriver && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-500">Driver Name</Label>
                        <p className="font-medium">{selectedDriver.name}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-500">Mobile Number</Label>
                        <p className="font-mono font-medium">{selectedDriver.mobile}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Parking - Car Submitted By */}
            {formData.audit_type === 'Parking' && (
              <div className="space-y-2">
                <Label htmlFor="submitted_by">Car Submitted By *</Label>
                <SearchableSelect
                  options={drivers.map((driver) => ({
                    value: driver.id,
                    label: `${driver.driver_id} - ${driver.name}`,
                    keywords: [driver.driver_id, driver.name, driver.mobile].filter(Boolean),
                  }))}
                  value={formData.submitted_by}
                  onChange={(val) => handleDriverSelect(val)}
                  placeholder="Select driver"
                  searchPlaceholder="Type driver ID, name, or mobile..."
                  emptyText="No matching drivers."
                  testId="submitted-by-select"
                />

                {/* Auto-filled Driver Details for Parking */}
                {selectedDriver && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-500">Driver Name</Label>
                        <p className="font-medium">{selectedDriver.name}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-500">Mobile Number</Label>
                        <p className="font-mono font-medium">{selectedDriver.mobile}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reason (for Parking and Repairs) */}
            {(formData.audit_type === 'Parking' || formData.audit_type === 'Repairs') && (
              <div className="space-y-2">
                <Label htmlFor="reason">
                  {formData.audit_type === 'Repairs' ? 'Repair Details' : 'Reason'}
                </Label>
                <textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                  placeholder={formData.audit_type === 'Repairs' ? 'Describe repair needed...' : 'Enter reason...'}
                  data-testid="reason-input"
                />
              </div>
            )}

            {/* Inventory Checklist */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-2">Inventory Checklist <span className="text-red-500">*</span></h4>
              <p className="text-xs text-gray-500 mb-3">
                All inventory fields are mandatory. If any item differs from the last audit of this car, you&apos;ll be asked to confirm before submitting.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="grid grid-cols-2 gap-2 p-2 border rounded-md bg-gray-50/50">
                    <div className="col-span-2 text-xs font-semibold text-gray-600">Tyre {n} *</div>
                    <Input
                      placeholder="Brand"
                      value={formData[`tyre${n}_brand`] || ''}
                      onChange={(e) => setFormData({ ...formData, [`tyre${n}_brand`]: e.target.value })}
                      className="h-9 text-sm"
                      required
                      data-testid={`audit-tyre${n}-brand`}
                    />
                    <Input
                      placeholder="Number"
                      value={formData[`tyre${n}_number`] || ''}
                      onChange={(e) => setFormData({ ...formData, [`tyre${n}_number`]: e.target.value })}
                      className="h-9 text-sm font-mono"
                      required
                      data-testid={`audit-tyre${n}-number`}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2 p-2 border rounded-md bg-gray-50/50">
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Stepney *</div>
                  <Input
                    placeholder="Brand"
                    value={formData.stepney_brand || ''}
                    onChange={(e) => setFormData({ ...formData, stepney_brand: e.target.value })}
                    className="h-9 text-sm"
                    required
                    data-testid="audit-stepney-brand"
                  />
                  <Input
                    placeholder="Number"
                    value={formData.stepney_number || ''}
                    onChange={(e) => setFormData({ ...formData, stepney_number: e.target.value })}
                    className="h-9 text-sm font-mono"
                    required
                    data-testid="audit-stepney-number"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 p-2 border rounded-md bg-gray-50/50">
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Battery *</div>
                  <Input
                    placeholder="Brand"
                    value={formData.battery_brand || ''}
                    onChange={(e) => setFormData({ ...formData, battery_brand: e.target.value })}
                    className="h-9 text-sm"
                    required
                    data-testid="audit-battery-brand"
                  />
                  <Input
                    placeholder="Number"
                    value={formData.battery_number || ''}
                    onChange={(e) => setFormData({ ...formData, battery_number: e.target.value })}
                    className="h-9 text-sm font-mono"
                    required
                    data-testid="audit-battery-number"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs font-semibold text-gray-600">Tool Kit *</Label>
                <select
                  value={formData.tool_kit_details || ''}
                  onChange={(e) => setFormData({ ...formData, tool_kit_details: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1 input-focus"
                  required
                  data-testid="audit-toolkit"
                >
                  <option value="">Select — Yes / No</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            {/* Photo Uploads */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Upload Photos</h4>
              <div className="space-y-4">
                {/* Car Side Photos */}
                <div>
                  <Label className="mb-2 block">Car Side Photos (4 photos)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((num) => (
                      <div key={num} className="space-y-2">
                        <Label htmlFor={`car_side_${num}`} className="text-sm text-gray-600">
                          Side {num}
                        </Label>
                        <Input
                          id={`car_side_${num}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange(`car_side_${num}`, e)}
                          data-testid={`car-side-${num}-upload`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Driver with Car Photo */}
                <div className="space-y-2">
                  <Label htmlFor="driver_with_car">Driver with Car Photo</Label>
                  <Input
                    id="driver_with_car"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('driver_with_car', e)}
                    data-testid="driver-with-car-upload"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={submitting}
              data-testid="submit-audit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
              Submit Audit
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inventory Mismatch Confirmation Dialog */}
      <Dialog
        open={diffDialog.open}
        onOpenChange={(o) => { if (!o) setDiffDialog({ open: false, diffs: [], previous: null }); }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="audit-diff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Inventory Differs from Last Audit
            </DialogTitle>
            <DialogDescription>
              The following items have changed since the last audit for this vehicle. Please verify before submitting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {diffDialog.diffs.map((d, i) => (
              <div key={i} className="border rounded-md p-2 bg-amber-50/40" data-testid={`diff-row-${i}`}>
                <div className="text-xs font-semibold text-gray-700 mb-1">{d.field}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Previous:</span>{' '}
                    <span className="font-mono line-through text-gray-500">{d.previous}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current:</span>{' '}
                    <span className="font-mono font-semibold text-emerald-700">{d.current}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDiffDialog({ open: false, diffs: [], previous: null })}
              disabled={submitting}
              data-testid="diff-cancel-btn"
            >
              No, Cancel
            </Button>
            <Button
              onClick={submitAuditNow}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="diff-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, Submit Audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Audits;
